import { useCallback, useEffect, useState } from "react";
import Errors, { getFieldErrors } from "../../models/errors";
import { createItem, deleteItem, getItem, updateItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, successAlert, warningAlert } from "../utils/messages";
import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSelect,
} from "@coreui/react";
import { FieldErrors } from "../form/FieldErrors";
import { emptyValueOnUndefined } from "../../utils/fields";
import { Spinner } from "react-bootstrap";
import Loading from "../indicators/Loading";
import TaxPayer from "../../models/tax-payer";
import TaxPayerElectronicDocumentExtraGrant, {
  EXTRA_GRANT_PERIOD_CHOICES,
  EXTRA_GRANT_PERIOD_MONTHLY,
  EXTRA_GRANT_PERIOD_YEARLY,
} from "../../models/tax-payer-electronic-document-extra-grant";

interface TaxPayerExtraGrantsFormProps {
  taxPayer?: TaxPayer;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const MONTH_LABELS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const formatPeriod = (grant: TaxPayerElectronicDocumentExtraGrant) => {
  if (grant.periodType === EXTRA_GRANT_PERIOD_MONTHLY && grant.month != null) {
    const label = MONTH_LABELS[grant.month - 1] ?? String(grant.month);
    return `${label} ${grant.year}`;
  }
  return `Año ${grant.year}`;
};

const formatUser = (grant: TaxPayerElectronicDocumentExtraGrant) => {
  const u = grant.createdByUser;
  if (!u) return "-";
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.email || `#${u.id}`;
};

const TaxPayerExtraGrantsForm: React.FC<TaxPayerExtraGrantsFormProps> = ({
  taxPayer,
  onCancel,
  onSuccess,
}) => {
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [grants, setGrants] = useState<TaxPayerElectronicDocumentExtraGrant[]>(
    []
  );

  const now = new Date();
  const [form, setForm] = useState<TaxPayerElectronicDocumentExtraGrant>({
    periodType: EXTRA_GRANT_PERIOD_MONTHLY,
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    extraDesAmount: 0,
    extraReceiptsAmount: 0,
  });

  const loadGrants = useCallback(async () => {
    if (taxPayer === undefined || taxPayer.id === undefined) {
      setGrants([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const requestStatus = await getItem<
      TaxPayerElectronicDocumentExtraGrant[]
    >(`/tax_payers/${taxPayer.id}/extra_grants/`);
    if (requestStatus.status !== SUCCESS) {
      warningAlert(requestStatus.detail ?? "Error cargando concesiones");
      setGrants([]);
    } else {
      setGrants(requestStatus.data ?? []);
    }
    setLoading(false);
  }, [taxPayer]);

  useEffect(() => {
    loadGrants();
  }, [loadGrants]);

  const clearForm = () => {
    setForm({
      periodType: EXTRA_GRANT_PERIOD_MONTHLY,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      extraDesAmount: 0,
      extraReceiptsAmount: 0,
    });
    setErrors({});
  };

  const onPeriodTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const periodType = e.target.value;
    setForm((prev) => ({
      ...prev,
      periodType,
      month:
        periodType === EXTRA_GRANT_PERIOD_YEARLY ? null : now.getMonth() + 1,
    }));
  };

  const onYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value, 10);
    setForm((prev) => ({
      ...prev,
      year: isNaN(parsed) ? undefined : parsed,
    }));
  };

  const onMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value, 10);
    setForm((prev) => ({
      ...prev,
      month: isNaN(parsed) ? undefined : parsed,
    }));
  };

  const onExtraAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value, 10);
    setForm((prev) => ({
      ...prev,
      extraDesAmount: isNaN(parsed) ? undefined : parsed,
    }));
  };

  const onExtraReceiptsAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const parsed = parseInt(e.target.value, 10);
    setForm((prev) => ({
      ...prev,
      extraReceiptsAmount: isNaN(parsed) ? undefined : parsed,
    }));
  };

  const onNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, note: e.target.value }));
  };

  const onSave = useCallback(async () => {
    if (taxPayer === undefined || taxPayer.id === undefined) return;
    setSubmitting(true);
    const payload: TaxPayerElectronicDocumentExtraGrant = {
      periodType: form.periodType,
      year: form.year,
      month:
        form.periodType === EXTRA_GRANT_PERIOD_MONTHLY ? form.month : null,
      extraDesAmount: form.extraDesAmount ?? 0,
      extraReceiptsAmount: form.extraReceiptsAmount ?? 0,
      note: form.note && form.note.length > 0 ? form.note : null,
    };

    const requestStatus = await createItem(
      `/tax_payers/${taxPayer.id}/extra_grants/`,
      payload
    );

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }
      errorAlert(requestStatus.detail ?? "Ha ocurrido un error!!");
      setSubmitting(false);
      return;
    }

    successAlert("Concesión creada");
    clearForm();
    await loadGrants();
    setSubmitting(false);
    onSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer, form, loadGrants]);

  const onToggleActive = useCallback(
    async (grant: TaxPayerElectronicDocumentExtraGrant) => {
      if (taxPayer === undefined || taxPayer.id === undefined) return;
      if (grant.id === undefined) return;
      const requestStatus = await updateItem(
        `/tax_payers/${taxPayer.id}/extra_grants/${grant.id}/toggle/`,
        {}
      );
      if (requestStatus.status !== SUCCESS) {
        errorAlert(requestStatus.detail ?? "Error al cambiar estado");
        return;
      }
      await loadGrants();
    },
    [taxPayer, loadGrants]
  );

  const onDelete = useCallback(
    async (grant: TaxPayerElectronicDocumentExtraGrant) => {
      if (taxPayer === undefined || taxPayer.id === undefined) return;
      if (grant.id === undefined) return;
      if (!window.confirm("¿Eliminar esta concesión?")) return;
      const requestStatus = await deleteItem(
        `/tax_payers/${taxPayer.id}/extra_grants/${grant.id}/`,
        {}
      );
      if (requestStatus.status !== SUCCESS) {
        errorAlert(requestStatus.detail ?? "Error al eliminar");
        return;
      }
      successAlert("Concesión eliminada");
      await loadGrants();
    },
    [taxPayer, loadGrants]
  );

  if (loading) {
    return <Loading height="30vh" />;
  }

  return (
    <>
      <fieldset disabled={submitting}>
        <h5>Nueva Concesión Extra</h5>
        <CFormGroup>
          <CRow>
            <CCol md={3}>
              <CLabel>Tipo de Período:</CLabel>
            </CCol>
            <CCol>
              <CSelect
                type="text"
                value={emptyValueOnUndefined(form.periodType)}
                onChange={onPeriodTypeChange}
              >
                {Array.from(EXTRA_GRANT_PERIOD_CHOICES.entries()).map(
                  (entry) => (
                    <option key={entry[0]} value={entry[0]}>
                      {entry[1]}
                    </option>
                  )
                )}
              </CSelect>
              <FieldErrors
                errors={getFieldErrors("periodType", errors) as string[]}
              />
            </CCol>
          </CRow>
        </CFormGroup>

        <CFormGroup>
          <CRow>
            <CCol md={3}>
              <CLabel>
                <span className="text-danger">*</span> Año:
              </CLabel>
            </CCol>
            <CCol>
              <CInput
                type="number"
                placeholder="Año"
                value={emptyValueOnUndefined(form.year)}
                onChange={onYearChange}
              />
              <FieldErrors
                errors={getFieldErrors("year", errors) as string[]}
              />
            </CCol>
          </CRow>
        </CFormGroup>

        {form.periodType === EXTRA_GRANT_PERIOD_MONTHLY && (
          <CFormGroup>
            <CRow>
              <CCol md={3}>
                <CLabel>
                  <span className="text-danger">*</span> Mes:
                </CLabel>
              </CCol>
              <CCol>
                <CSelect
                  type="text"
                  value={emptyValueOnUndefined(form.month ?? undefined)}
                  onChange={onMonthChange}
                >
                  <option value="" disabled>
                    -----
                  </option>
                  {MONTH_LABELS.map((label, idx) => (
                    <option key={idx + 1} value={idx + 1}>
                      {label}
                    </option>
                  ))}
                </CSelect>
                <FieldErrors
                  errors={getFieldErrors("month", errors) as string[]}
                />
              </CCol>
            </CRow>
          </CFormGroup>
        )}

        <CFormGroup>
          <CRow>
            <CCol md={3}>
              <CLabel>Documentos Extra (DEs):</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="number"
                placeholder="Cantidad extra"
                value={emptyValueOnUndefined(form.extraDesAmount)}
                onChange={onExtraAmountChange}
              />
              <FieldErrors
                errors={getFieldErrors("extraDesAmount", errors) as string[]}
              />
            </CCol>
          </CRow>
        </CFormGroup>

        <CFormGroup>
          <CRow>
            <CCol md={3}>
              <CLabel>Recibos Extra:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="number"
                placeholder="Cantidad extra"
                value={emptyValueOnUndefined(form.extraReceiptsAmount)}
                onChange={onExtraReceiptsAmountChange}
              />
              <FieldErrors
                errors={
                  getFieldErrors("extraReceiptsAmount", errors) as string[]
                }
              />
            </CCol>
          </CRow>
        </CFormGroup>

        <CFormGroup>
          <CRow>
            <CCol md={3}>
              <CLabel>Nota:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                placeholder="Observación / motivo"
                value={emptyValueOnUndefined(form.note ?? undefined)}
                onChange={onNoteChange}
              />
              <FieldErrors
                errors={getFieldErrors("note", errors) as string[]}
              />
            </CCol>
          </CRow>
        </CFormGroup>

        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onCancel}>
              Salir
            </CButton>
            <CButton type="button" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : null}
              {submitting ? "Guardando..." : "Agregar Concesión"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>

      <div style={{ clear: "both" }} />
      <hr />
      <h5>Concesiones Registradas</h5>
      {grants.length === 0 ? (
        <p className="text-muted">Sin concesiones registradas</p>
      ) : (
        <table className="table table-sm table-striped">
          <thead>
            <tr>
              <th>Período</th>
              <th>DEs Extra</th>
              <th>Recibos Extra</th>
              <th>Nota</th>
              <th>Creado por</th>
              <th>Fecha</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {grants.map((g) => (
              <tr key={g.id}>
                <td>{formatPeriod(g)}</td>
                <td>{g.extraDesAmount ?? 0}</td>
                <td>{g.extraReceiptsAmount ?? 0}</td>
                <td>{g.note ?? "-"}</td>
                <td>{formatUser(g)}</td>
                <td>
                  {g.createdAt
                    ? new Date(g.createdAt).toLocaleString()
                    : "-"}
                </td>
                <td>
                  <span
                    className={`badge badge-${g.isActive !== false ? "success" : "secondary"}`}
                  >
                    {g.isActive !== false ? "Activo" : "Inactivo"}
                  </span>
                </td>
                <td>
                  <CButtonGroup>
                    <CButton
                      color={g.isActive !== false ? "warning" : "success"}
                      size="sm"
                      title={g.isActive !== false ? "Desactivar" : "Activar"}
                      onClick={() => onToggleActive(g)}
                    >
                      <i className={`fa fa-${g.isActive !== false ? "pause" : "play"}`}></i>
                    </CButton>
                    <CButton
                      color="danger"
                      size="sm"
                      onClick={() => onDelete(g)}
                    >
                      <i className="fa fa-trash"></i>
                    </CButton>
                  </CButtonGroup>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

export default TaxPayerExtraGrantsForm;
